import { Col, Row, Space } from "antd";
import { DeleteWithConfirmationButton } from "components/SeasonView/components/DeleteWithConfirmationButton";
import { EditModeToggle } from "components/Shared/EditModeToggle";
import { RosterAndStaffTable } from "components/Shared/RosterAndStaffTable";
import { SearchInput } from "components/Shared/SearchInput";
import { PeopleTypes } from "enums/peopleTypes";
import { useRecordsState } from "hooks/useRecordsState";
import { useAddSeasonStaffPersons, useDeleteSeasonStaffPersons } from "hooks/useSeasonStaffApi";
import { useUpdateSeasonStaffPerson } from "hooks/useUpdateStaffPerson";
import { useState } from "react";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { filterPersonList } from "utils/filterPersonList";
import { getPersonDeleteApiData } from "utils/getPersonDeleteApiData";
import { getPersonRecordKey } from "utils/getPersonRecordKey";
import { runAsyncAction } from "utils/runAsyncAction";
import { useAddPeopleStaffColumns } from "../hooks/useAddPeopleStaffColumns";
import { useSeasonStaffColumns } from "../hooks/useSeasonStaffColumns";
import { AddPeople } from "./AddPeople";
import { CopyPeople } from "./CopyPeople";
import UploadPersonImageModal from "./Images/UploadPersonImageModal";

type Props = {
  isLoading: boolean;
  season: any;
  staff: Array<any>;
  viewOnly: boolean;
};

export const SeasonStaffTab = ({ isLoading, season, staff, viewOnly }: Props) => {
  const { formatMessage } = useIntl();
  const { entityId: entityIdFromParams, seasonId: seasonIdFromParams } = useParams();
  const entityId = entityIdFromParams || "undefined";
  const seasonId = seasonIdFromParams || "undefined";
  const [searchText, setSearchText] = useState("");
  const [editMode, setEditMode] = useState(false);
  const [imageUploadPerson, setImageUploadPerson] = useState<object>();
  const [selectedRowRecords, setSelectedRowRecords] = useState([]);
  const filteredStaff = filterPersonList(staff, searchText);
  const selectedRowKeys = selectedRowRecords.map(getPersonRecordKey);
  const { mutateAsync: addRecordsApi, isPending: areRecordsAdding } = useAddSeasonStaffPersons();
  const { mutateAsync: deleteRecordsApi, isPending: areRecordsDeleting } = useDeleteSeasonStaffPersons();
  const { mutateAsync: updateRecord, updatingRecord, isUpdating: isRecordBeingUpdated } = useUpdateSeasonStaffPerson();

  const { checkIfRecordBeingBusy, ensureRecordsBusy } = useRecordsState({
    isAddingOrDeleting: areRecordsAdding || areRecordsDeleting,
    isUpdating: isRecordBeingUpdated,
    updatingRecord: updatingRecord,
    applyForAll: !season?.taskStatus,
  });

  const areUserControlsDisabled =
    isLoading || areRecordsAdding || areRecordsDeleting || !season?.areRosterAndStaffEditable;

  const handleUpdateRecord = (data: any, record: any) =>
    updateRecord({
      entityId,
      seasonId,
      personId: record?.personId,
      dcPersonId: record?.dcPersonId,
      data,
      record,
    });
  const isViewOnly = viewOnly || !season?.areRosterAndStaffEditable;
  const imageClickHandler = isViewOnly ? undefined : (person: any) => setImageUploadPerson(person);

  const columns = useSeasonStaffColumns({
    viewOnly: isViewOnly,
    handlePersonImageClick: imageClickHandler,
    editMode,
    checkIfRecordBeingBusy,
    updateRecord: (data, record) => {
      runAsyncAction(handleUpdateRecord(data, record), "Failed to update season staff record");
    },
  });

  const addPeopleColumns = useAddPeopleStaffColumns({
    onAdd: (record: any) => {
      runAsyncAction(
        ensureRecordsBusy([record], async () =>
          addRecordsApi({
            entityId,
            seasonId,
            data: {
              persons: [record],
            },
          })
        ),
        "Failed to add season staff record"
      );
    },
    onDelete: (record: any) => {
      runAsyncAction(
        ensureRecordsBusy([record], async () =>
          deleteRecordsApi({
            entityId,
            seasonId,
            data: getPersonDeleteApiData([record]),
          })
        ),
        "Failed to delete season staff record"
      );
    },
    checkIfRecordBeingBusy,
    getAlreadyAdded: (record: any) =>
      staff.find((x: any) => x.personId === record.personId && x.dcPersonId === record.dcPersonId),
  });

  const onDeleteSelectedRows = () => {
    runAsyncAction(
      ensureRecordsBusy(selectedRowRecords, async () =>
        deleteRecordsApi(
          { entityId, seasonId, data: getPersonDeleteApiData(selectedRowRecords) },
          {
            onSuccess: () => setSelectedRowRecords([]),
          }
        )
      ),
      "Failed to delete selected season staff records"
    );
  };

  const onEditModeChange = (value: boolean) => {
    setEditMode(value);
    if (!value) {
      setSelectedRowRecords([]);
    }
  };

  return (
    <>
      <UploadPersonImageModal
        person={imageUploadPerson}
        handleClose={() => setImageUploadPerson(undefined)}
        updatePerson={async (data) => {
          await handleUpdateRecord(data, imageUploadPerson);
          setImageUploadPerson({ ...imageUploadPerson, ...data });
        }}
      />
      <Row className="mb-10" align="middle">
        <Col span={8}>
          {selectedRowRecords.length > 0 && (
            <DeleteWithConfirmationButton
              disabled={areUserControlsDisabled}
              count={selectedRowRecords.length}
              onDelete={onDeleteSelectedRows}
            />
          )}
        </Col>
        <Col span={16} style={{ display: "flex", justifyContent: "flex-end" }}>
          <Space>
            {!viewOnly && (
              <>
                <EditModeToggle disabled={areUserControlsDisabled} onChange={onEditModeChange} />
                <CopyPeople
                  type={PeopleTypes.Staff}
                  disabled={areUserControlsDisabled}
                  entityId={entityId}
                  currentSeasonId={seasonId}
                />
                <AddPeople
                  disabled={areUserControlsDisabled}
                  entityId={entityId}
                  title={formatMessage({ id: "button.addPerson" })}
                  columns={addPeopleColumns}
                />
              </>
            )}
            <SearchInput onSearch={setSearchText} />
          </Space>
        </Col>
      </Row>
      <Row>
        <RosterAndStaffTable
          isLoading={isLoading || areRecordsAdding || areRecordsDeleting}
          records={filteredStaff}
          columns={columns}
          rowSelection={
            (!viewOnly && editMode
              ? {
                  selectedRowKeys,
                  onChange: (_: any, rowRecords: Array<any>) => setSelectedRowRecords(rowRecords as any),
                }
              : null) as any
          }
        />
      </Row>
    </>
  );
};
